import * as React from 'react';
import { View, ScrollView, Linking } from 'react-native';
import GrainCumulativeAnalysisTable from '../../components/GrainCumulativeAnalysisTable';
import ParticleDistributionTable from '../../components/ParticleDistributionTable';
import { IStore } from '../../redux/reducers';
import { useSelector, useDispatch } from 'react-redux';
import { JobStatus } from '../../models';
import { Box, Text, Button, HStack, VStack, Badge, useColorModeValue, Spinner, Center } from "native-base";
import { useEffect } from 'react';
import GrainTopLevelAnalysisTable from 'src/components/GrainTopLevelAnalysisTable';
import { containerStyle } from 'src/styles/containerStyle';
import DownloadResultButtons from 'src/components/DownloadResultButtons';
import JobInfoEditor from 'src/components/JobInfoEditor';
import { useIsFocused } from '@react-navigation/native';
import DisplayCsvTableComponentV2 from 'src/components/DisplayCsvTableComponentV2';
import { MANUAL_GRAIN_ANALYSIS } from 'src/constants';
import {getText} from 'src/utils/i18n';
import * as JobActions from '../../redux/actions/jobAction';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import ShowTransactionModal from 'src/components/modal/ShowTransactionModal';
import { isAveragingJob, getDisplayableJobId } from 'src/utils/jobUtils';
import * as GrainAPI from 'src/api/grain';
import {
    getRejectSelectionSessionKey,
} from 'src/utils/rejectConfig';
import { isLightboxFeatureEnabled, isRiceLikeGrain } from 'src/utils/grainFeatureUtils';
import { isElectron } from 'src/utils/miscUtils';
import {
    IRejectConfigElectronApi,
    getRejectConfigStateKeys,
    loadCurrentGrainRejectionMapping,
    loadCurrentProfileRejectConfig,
    resolveEffectiveRejectSelectionConfig,
} from 'src/utils/profileRejectConfig';

export default function ResultsPage({ route }: any) {

    const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
    const [showBlockchainButtons, setShowBlockchainButtons] = React.useState(false);
    const dispatch = useDispatch();

    const grains = useSelector((store: IStore) => store.user.userProfile.response?.grains);
    const userProfileSettings = useSelector((store: IStore) => store.user.userProfile.response?.settings);
    const lightboxEnabled = isLightboxFeatureEnabled(userProfileSettings);
    const resultEditEnabled = userProfileSettings?.result_edit_enabled || false;
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const userSettings = useSelector((store: IStore) => store.user.userSettings);
    const textColor = useColorModeValue('coolGray.800', 'coolGray.100');
    const averagingBoxBg = useColorModeValue('info.50', 'coolGray.700');
    const averagingBoxBorder = useColorModeValue('info.200', 'coolGray.600');
    const averagingSourceText = useColorModeValue('coolGray.700', 'coolGray.300');
    const job = useSelector((store: IStore) => store.job.jobStatus.job);
    const { mappingStateKey, profileStateKey } = getRejectConfigStateKeys({
        jobId: job?.jobId,
        grainId: job?.grainId,
        profileId: job?.profileId,
    });
    const [grainRejectionMappingState, setGrainRejectionMappingState] = React.useState<{
        grainId: string;
        loaded: boolean;
        mapping?: Record<string, string>;
    }>({ grainId: '', loaded: false });
    const [profileRejectConfigState, setProfileRejectConfigState] = React.useState<{
        identity: string;
        loaded: boolean;
        config?: {
            particleDistributionRejectKeys?: string[];
            rejectedGrainsRejectKeys?: string[];
        };
    }>({ identity: '', loaded: false });
    const grainRejectionMapping = grainRejectionMappingState.grainId === mappingStateKey
        ? grainRejectionMappingState.mapping
        : undefined;
    const profileRejectConfig = profileRejectConfigState.identity === profileStateKey
        ? profileRejectConfigState.config
        : undefined;
    const isProfileRejectConfigLoading = Boolean(
        job?.grainId
        && job?.profileId
        && !(profileRejectConfigState.identity === profileStateKey && profileRejectConfigState.loaded)
    );
    const jobStatus = useSelector((store: IStore) => store.job.currentJobStatus);
    const isLoadingJob = useSelector((store: IStore) => store.job.jobStatus.isFetching);
    const canEditResults = (resultEditEnabled || job?.productName === MANUAL_GRAIN_ANALYSIS) && (job?.processingMode === 'local' || isOnline);
    const canEditParticleDistribution = canEditResults &&
        job?.results?.indi_grain_csv !== undefined &&
        job?.results?.indi_grain_csv !== "";
    const canComputeParticleDistribution = job?.results?.indi_grain_csv !== undefined &&
        job?.results?.indi_grain_csv !== "";
    const grainName = React.useMemo(
        () => grains?.find((grain: { grainId?: string; name?: string }) => grain.grainId?.toString() === job?.grainId?.toString())?.name,
        [grains, job?.grainId]
    );
    const isRice = React.useMemo(
        () => isRiceLikeGrain(job?.grainId, grainName),
        [job?.grainId, grainName]
    );
    const sessionKey = React.useMemo(
        () => getRejectSelectionSessionKey(job?.grainId, job?.profileId),
        [job?.grainId, job?.profileId]
    );
    const storedRejectSelectionConfig = userSettings?.[sessionKey];
    const jobRejectionMappings = React.useMemo(() => {
        const mappings = job?.additionalAttributes?.rejectionMappings;
        return mappings && Object.keys(mappings).length > 0 ? mappings : undefined;
    }, [job?.additionalAttributes?.rejectionMappings]);

    React.useEffect(() => {
        let cancelled = false;
        const loadMapping = async () => {
            setGrainRejectionMappingState({ grainId: mappingStateKey, loaded: false });
            if (jobRejectionMappings) {
                setGrainRejectionMappingState({ grainId: mappingStateKey, loaded: true, mapping: jobRejectionMappings });
                return;
            }

            if (!job?.grainId) {
                setGrainRejectionMappingState({ grainId: mappingStateKey, loaded: true });
                return;
            }

            const electron = isElectron() && typeof window !== 'undefined'
                ? (window as Window & { electron?: IRejectConfigElectronApi }).electron
                : undefined;
            const mapping = await loadCurrentGrainRejectionMapping({
                grainId: String(job.grainId),
                getLocalGrains: electron?.getGrains
                    ? () => electron.getGrains()
                    : undefined,
                getCloudMapping: isOnline !== false
                    ? GrainAPI.getRejectionMapping
                    : undefined,
            });
            if (!cancelled) {
                setGrainRejectionMappingState({ grainId: mappingStateKey, loaded: true, mapping });
            }
        };

        loadMapping();
        return () => {
            cancelled = true;
        };
    }, [mappingStateKey, isOnline, job?.grainId, jobRejectionMappings]);

    React.useEffect(() => {
        let cancelled = false;

        const loadProfileRejectConfig = async () => {
            setProfileRejectConfigState({ identity: profileStateKey, loaded: false });
            if (!job?.grainId || !job?.profileId) {
                setProfileRejectConfigState({ identity: profileStateKey, loaded: true });
                return;
            }

            const electron = isElectron() && typeof window !== 'undefined'
                ? (window as Window & { electron?: IRejectConfigElectronApi }).electron
                : undefined;
            const matchedProfile = await loadCurrentProfileRejectConfig({
                grainId: String(job.grainId),
                profileId: String(job.profileId),
                getLocalProfiles: electron?.getGrainProfiles
                    ? (args) => electron.getGrainProfiles(args)
                    : undefined,
                getCloudProfiles: isOnline !== false
                    ? GrainAPI.getGrainProfiles
                    : undefined,
            });
            if (!cancelled) {
                setProfileRejectConfigState({
                    identity: profileStateKey,
                    loaded: true,
                    config: matchedProfile ? {
                        particleDistributionRejectKeys: matchedProfile.particleDistributionRejectKeys,
                        rejectedGrainsRejectKeys: matchedProfile.rejectedGrainsRejectKeys,
                    } : undefined,
                });
            }
        };

        loadProfileRejectConfig();
        return () => {
            cancelled = true;
        };
    }, [isOnline, job?.grainId, job?.profileId, profileStateKey]);

    const rejectionCalculationConfig = React.useMemo(() => {
        if (!job?.grainId || !job?.profileId) {
            return undefined;
        }
        if (isProfileRejectConfigLoading) {
            return undefined;
        }
        return resolveEffectiveRejectSelectionConfig({
            grainId: job.grainId,
            profileId: job.profileId,
            rejectionMapping: grainRejectionMapping,
            profile: profileRejectConfig,
            storedConfig: storedRejectSelectionConfig,
            jobConfig: job.results?.rejectionCalculationConfig,
        });
    }, [grainRejectionMapping, isProfileRejectConfigLoading, job?.grainId, job?.profileId, job?.results?.rejectionCalculationConfig, profileRejectConfig, storedRejectSelectionConfig]);

    useEffect(() => {
        const jobIdFromRoute = route?.params?.jobId;
        if (jobIdFromRoute && jobIdFromRoute !== job?.jobId) {
            dispatch(JobActions.setJobId(jobIdFromRoute));
            dispatch(JobActions.pollJobStatus.request(jobIdFromRoute));
        }
    }, [route?.params?.jobId]);

    const isFocused = useIsFocused();

    const getTransactionUrl = () => {
        if (job?.txHash) {
            return `https://hashscan.io/testnet/transaction/${job.txHash}`;
        }
        return '';
    };

    const handleViewOnBlockchain = () => {
        const url = getTransactionUrl();
        if (url) {
            Linking.openURL(url).catch(err => console.error('Failed to open URL:', err));
        }
    };

    const handleOpenShareModal = () => {
        setIsShareModalOpen(true);
    };

    if (!isFocused) {
        return <Box variant={"main"} style={containerStyle.mainContainer}>
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Text color={textColor} style={{ padding: 12, textAlign: 'center' }}>{getText('loading')}</Text>
            </View>
        </Box>
    }

    return (
        <Box variant={"main"} style={containerStyle.mainContainer}>
            {isLoadingJob || isProfileRejectConfigLoading ? (
                <Center flex={1}>
                    <VStack space={4} alignItems="center">
                        <Spinner size="lg" />
                        <Text color={textColor}>{getText('loading')}</Text>
                    </VStack>
                </Center>
            ) : (jobStatus == JobStatus.JOB_COMPLETED && job) ? (<>
                <ScrollView>
                    <View style={{ flexDirection: 'column' }}>
                        {job?.txHash && (
                            <Box mb={2}>
                                <Button
                                    variant="subtle"
                                    colorScheme="success"
                                    leftIcon={<MaterialCommunityIcons name="shield-check" size={20} color="#27AE60" />}
                                    onPress={() => setShowBlockchainButtons(!showBlockchainButtons)}
                                    mb={2}
                                >
                                    <Text fontSize="md" fontWeight="bold" color="#27AE60">
                                        Verified on Blockchain
                                    </Text>
                                </Button>
                                {showBlockchainButtons && (
                                    <HStack
                                        justifyContent="center"
                                        space={2}
                                        mb={2}
                                    >
                                        <Button
                                            size="sm"
                                            leftIcon={<MaterialCommunityIcons name="link-variant" size={16} color="white" />}
                                            onPress={handleViewOnBlockchain}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            size="sm"
                                            leftIcon={<MaterialCommunityIcons name="share-variant" size={16} color="white" />}
                                            onPress={handleOpenShareModal}
                                        >
                                            Share
                                        </Button>
                                    </HStack>
                                )}
                            </Box>
                        )}
                        <DownloadResultButtons
                            rejectionCalculationConfig={rejectionCalculationConfig}
                            rejectionMapping={grainRejectionMapping}
                        />
                        {isAveragingJob(job) && job.additionalAttributes?.sourceJobIds && (
                            <Box mt={4} mb={4} p={4} bg={averagingBoxBg} borderRadius="md" borderWidth={1} borderColor={averagingBoxBorder}>
                                <HStack alignItems="center" mb={2}>
                                    <Badge colorScheme="info" variant="solid" mr={2}>
                                        Average
                                    </Badge>
                                    <Text fontWeight="bold" fontSize="md">
                                        Averaged from {job.additionalAttributes.sourceJobIds.length} jobs
                                    </Text>
                                </HStack>
                                <VStack space={1} mt={2}>
                                    {job.additionalAttributes.sourceJobIds.map((sourceJobId, index) => (
                                        <Text key={sourceJobId} fontSize="sm" color={averagingSourceText}>
                                            {index + 1}. Job ID: {getDisplayableJobId(sourceJobId)}
                                        </Text>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                        <GrainTopLevelAnalysisTable
                            rejectionCalculationConfig={rejectionCalculationConfig}
                            rejectionMapping={grainRejectionMapping}
                        />
                        {lightboxEnabled ? (
                            <>
                                {isRice ? (
                                    <>
                                        {!canComputeParticleDistribution &&
                                            job.results?.particle_distribution_csv &&
                                            <DisplayCsvTableComponentV2
                                                title={getText('particleDistn')}
                                                isEditable={false}
                                                columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                result_field={"particle_distribution_csv"}
                                                rejectionCalculationConfig={rejectionCalculationConfig}
                                                csvFileUrl={job.results?.particle_distribution_csv} />}
                                        {!canComputeParticleDistribution &&
                                            (job.results as any)?.particle_distribution_width_csv &&
                                            <DisplayCsvTableComponentV2
                                                title={getText('particleDistnByWidth')}
                                                isEditable={false}
                                                columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                result_field={"particle_distribution_width_csv"}
                                                rejectionCalculationConfig={rejectionCalculationConfig}
                                                csvFileUrl={(job.results as any)?.particle_distribution_width_csv} />}
                                        {canComputeParticleDistribution && (
                                            <>
                                                <ParticleDistributionTable
                                                    readOnly={!canEditParticleDistribution}
                                                    jobId={job.jobId}
                                                    grainId={job.grainId}
                                                    profileId={job.profileId}
                                                    rejectionCalculationConfig={rejectionCalculationConfig}
                                                    title={getText('particleDistn')}
                                                    colIndex={1}
                                                    lower={0}
                                                    upper={30}
                                                    stepSize={6}
                                                    csvUrl={job.results?.indi_grain_csv} />
                                                <ParticleDistributionTable
                                                    readOnly={!canEditParticleDistribution}
                                                    jobId={job.jobId}
                                                    grainId={job.grainId}
                                                    profileId={job.profileId}
                                                    rejectionCalculationConfig={rejectionCalculationConfig}
                                                    title={getText('particleDistnByWidth')}
                                                    colIndex={2}
                                                    lower={0}
                                                    upper={6}
                                                    stepSize={1}
                                                    csvUrl={job.results?.indi_grain_csv} />
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {!canComputeParticleDistribution &&
                                            (job.results as any)?.particle_distribution_area_csv &&
                                            <DisplayCsvTableComponentV2
                                                title={getText('particleDistnByArea')}
                                                isEditable={false}
                                                columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                result_field={"particle_distribution_area_csv"}
                                                rejectionCalculationConfig={rejectionCalculationConfig}
                                                csvFileUrl={(job.results as any)?.particle_distribution_area_csv} />}
                                        {canComputeParticleDistribution && (
                                            <ParticleDistributionTable
                                                readOnly={!canEditParticleDistribution}
                                                jobId={job.jobId}
                                                grainId={job.grainId}
                                                profileId={job.profileId}
                                                rejectionCalculationConfig={rejectionCalculationConfig}
                                                title={getText('particleDistnByArea')}
                                                colIndex={3}
                                                lower={0}
                                                upper={30}
                                                stepSize={6}
                                                csvUrl={job.results?.indi_grain_csv} />
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {
                                    job.results?.cumulative_analysis_csv !== undefined &&
                                        job.results?.cumulative_analysis_csv !== "" ?
                                        <DisplayCsvTableComponentV2
                                            title={getText('cumulativeAnalysis')}
                                            columnsToDisplay={[0, 1, 2]}
                                            editableColumns={[1, 2]}
                                            isEditable={canEditResults}
                                            jobId={job.jobId}
                                            result_field={"cumulative_analysis_csv"}
                                            csvFileUrl={job.results?.cumulative_analysis_csv} />
                                        : <GrainCumulativeAnalysisTable />
                                }
                                {
                                    job.results?.rejection_analysis_display_csv !== undefined &&
                                        job.results?.rejection_analysis_display_csv !== "" ?
                                        <DisplayCsvTableComponentV2
                                            isEditable={false}
                                            title={getText('rejectionAnalysis')}
                                            columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                            result_field={"rejection_analysis_display_csv"}
                                            rejectionCalculationConfig={rejectionCalculationConfig}
                                            rejectionMapping={grainRejectionMapping}
                                            csvFileUrl={job.results?.rejection_analysis_display_csv} />
                                        : <DisplayCsvTableComponentV2
                                            title={getText('rejectionAnalysis')}
                                            isEditable={canEditResults}
                                            columnsToDisplay={[1, 2, 3, 4, 5]}
                                            editableColumns={[1, 3]}
                                            jobId={job.jobId}
                                            result_field={"rejection_analysis_csv"}
                                            total_kernels={job.results?.grain_count}
                                            rejectionCalculationConfig={rejectionCalculationConfig}
                                            rejectionMapping={grainRejectionMapping}
                                            csvFileUrl={job.results?.rejection_analysis_csv} />
                                }
                                {
                                    (
                                        !canComputeParticleDistribution &&
                                        job.results?.particle_distribution_csv !== undefined &&
                                        job.results?.particle_distribution_csv !== "") ?
                                        <>
                                            <DisplayCsvTableComponentV2
                                                title={getText('particleDistn')}
                                                isEditable={false}
                                                columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                result_field={"particle_distribution_csv"}
                                                rejectionCalculationConfig={rejectionCalculationConfig}
                                                csvFileUrl={job.results?.particle_distribution_csv} />
                                            {(job.results as any)?.particle_distribution_width_csv !== undefined &&
                                                (job.results as any)?.particle_distribution_width_csv !== "" &&
                                                <DisplayCsvTableComponentV2
                                                    title={getText('particleDistnByWidth')}
                                                    isEditable={false}
                                                    columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                    result_field={"particle_distribution_width_csv"}
                                                    rejectionCalculationConfig={rejectionCalculationConfig}
                                                    csvFileUrl={(job.results as any)?.particle_distribution_width_csv} />}
                                            {(job.results as any)?.particle_distribution_area_csv !== undefined &&
                                                (job.results as any)?.particle_distribution_area_csv !== "" &&
                                                <DisplayCsvTableComponentV2
                                                    title={getText('particleDistnByArea')}
                                                    isEditable={false}
                                                    columnsToDisplay={[0, 1, 2, 3, 4, 5]}
                                                    result_field={"particle_distribution_area_csv"}
                                                    rejectionCalculationConfig={rejectionCalculationConfig}
                                                    csvFileUrl={(job.results as any)?.particle_distribution_area_csv} />}
                                        </>
                                        : <>
                                            {
                                                canComputeParticleDistribution &&
                                                <>
                                                    <ParticleDistributionTable
                                                        readOnly={!canEditParticleDistribution}
                                                        jobId={job.jobId}
                                                        grainId={job.grainId}
                                                        profileId={job.profileId}
                                                        rejectionCalculationConfig={rejectionCalculationConfig}
                                                        title={getText('particleDistn')}
                                                        colIndex={1}
                                                        lower={0}
                                                        upper={30}
                                                        stepSize={6}
                                                        csvUrl={job.results?.indi_grain_csv} />
                                                    <ParticleDistributionTable
                                                        readOnly={!canEditParticleDistribution}
                                                        jobId={job.jobId}
                                                        grainId={job.grainId}
                                                        profileId={job.profileId}
                                                        rejectionCalculationConfig={rejectionCalculationConfig}
                                                        title={getText('particleDistnByWidth')}
                                                        colIndex={2}
                                                        lower={0}
                                                        upper={6}
                                                        stepSize={1}
                                                        csvUrl={job.results?.indi_grain_csv} />
                                                    <ParticleDistributionTable
                                                        readOnly={!canEditParticleDistribution}
                                                        jobId={job.jobId}
                                                        grainId={job.grainId}
                                                        profileId={job.profileId}
                                                        rejectionCalculationConfig={rejectionCalculationConfig}
                                                        title={getText('particleDistnByArea')}
                                                        colIndex={3}
                                                        lower={0}
                                                        upper={30}
                                                        stepSize={6}
                                                        csvUrl={job.results?.indi_grain_csv} />
                                                </>
                                            }
                                        </>
                                }
                            </>
                        )}
                        <JobInfoEditor job={job} />
                    </View>
                </ScrollView>
            </>) : (<>
                <View
                    style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Text
                        color={textColor}
                        style={{ padding: 12, textAlign: 'center' }}>{getText('selectImageTabNotice')}
                    </Text>
                </View>
            </>)}
            <ShowTransactionModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                jobId={job?.jobId}
                txHash={job?.txHash}
            />
        </Box>
    );

}
